import { DateTime } from 'luxon';
import type { WtrCode, WtrSeverity, WtrWarning } from './types';

/**
 * t166u: working-time & availability checks, consolidated. One group per warning type (code + the thing it is
 * about, e.g. which skill or leave status), the affected people under it with what differs per person (shift date,
 * expiry), ordered by severity then by number of people.
 */
/** `shift_ids` (t242u): the shifts behind this person's entry, so a list can open them (absent when none). */
export interface WarningPerson { user_id: string | null; details: string[]; shift_ids?: string[] }
export interface WarningGroup { key: string; code: WtrCode; severity: WtrSeverity; title: string; people: WarningPerson[]; count: number }

/** en-GB dates for checks: 19 Oct 2026; with the weekday for shift dates (Mon 19 Oct). */
export const checkDate = (iso: string) => { const d = DateTime.fromISO(iso.slice(0, 10)); return d.isValid ? d.setLocale('en-GB').toFormat('d LLL yyyy') : iso; };
const shiftDay = (iso: string) => { const d = DateTime.fromISO(iso.slice(0, 10)); return d.isValid ? d.setLocale('en-GB').toFormat('ccc d LLL') : iso; };

const TITLES: Partial<Record<WtrCode, string>> = {
  overlap: 'Overlapping shifts', rest_break: 'Needs a rest break', daily_rest: 'Not enough rest between shifts',
  weekly_rest: 'Not enough weekly rest', weekly_hours: 'Over 48h in a week', average_48h: 'Average over 48h a week',
  young_daily_limit: 'Young worker: over 8h in a day', young_weekly_limit: 'Young worker: over 40h in a week', young_night_work: 'Young worker: night work',
  unavailable: 'Unavailable', max_hours_preference: 'Over preferred weekly hours', closure_day: 'Closure day', duplicate: 'Duplicate shift', dst_adjusted: 'Clock change',
  contract_hours: 'Over contracted hours', contract_days: 'Over contracted days',
};
/** Codes whose message carries per-person numbers worth keeping as the detail line. */
const KEEP_MESSAGE = new Set<WtrCode>(['rest_break', 'daily_rest', 'weekly_rest', 'weekly_hours', 'average_48h', 'young_daily_limit', 'young_weekly_limit', 'young_night_work', 'max_hours_preference', 'dst_adjusted', 'contract_hours', 'contract_days']);
const RANK: Record<WtrSeverity, number> = { error: 0, warning: 1, info: 2 };
const str = (v: unknown) => (typeof v === 'string' && v ? v : null);

function classify(w: WtrWarning): { key: string; title: string; detail: string | null } {
  const d = w.details ?? {};
  const date = str(d.date);
  const day = date ? shiftDay(date) : null;
  const join = (...xs: (string | null)[]) => xs.filter(Boolean).join(' · ') || null;
  if (w.code === 'missing_skill') {
    const skill = str(d.skill_name) ?? w.message.match(/^Doesn't have (.+), which/)?.[1] ?? w.message.match(/^(.+) expired on/)?.[1] ?? 'a required skill';
    const exp = str(d.expired_on);
    return exp ? { key: `missing_skill:expired:${d.skill_id ?? skill}`, title: `${skill} expired`, detail: join(day, `expired ${checkDate(exp)}`) }
      : { key: `missing_skill:missing:${d.skill_id ?? skill}`, title: `Missing ${skill}`, detail: day };
  }
  if (w.code === 'on_leave') {
    const approved = d.status ? d.status === 'approved' : /^On approved/.test(w.message);
    return { key: `on_leave:${approved ? 'approved' : 'requested'}`, title: approved ? 'On approved leave' : 'Leave requested', detail: join(str(d.leave_name), day) };
  }
  if (w.code === 'position_not_at_building') {
    const pos = str(d.position_name) ?? 'Position', at = str(d.location_name) ?? 'this building';
    return { key: `position_not_at_building:${d.position_id ?? pos}:${d.location_id ?? at}`, title: `${pos} not allowed at ${at}`, detail: day };
  }
  if (w.code === 'unavailable') return { key: 'unavailable', title: TITLES.unavailable!, detail: join(day, str(d.note)) };
  if (w.code === 'closure_day') return { key: 'closure_day', title: TITLES.closure_day!, detail: join(day, str(d.closure)) };
  const title = TITLES[w.code] ?? w.message;
  return { key: w.code, title, detail: KEEP_MESSAGE.has(w.code) ? join(day, w.message) : day };
}

export function groupWarnings(warnings: WtrWarning[]): WarningGroup[] {
  const groups = new Map<string, WarningGroup & { by: Map<string, WarningPerson> }>();
  for (const w of warnings) {
    const c = classify(w);
    let g = groups.get(c.key);
    if (!g) { g = { key: c.key, code: w.code, severity: w.severity, title: c.title, people: [], count: 0, by: new Map() }; groups.set(c.key, g); }
    if (RANK[w.severity] < RANK[g.severity]) g.severity = w.severity;
    g.count++;
    const pk = w.user_id ?? '';
    let p = g.by.get(pk);
    if (!p) { p = { user_id: w.user_id, details: [] }; g.by.set(pk, p); g.people.push(p); }
    if (c.detail && !p.details.includes(c.detail)) p.details.push(c.detail);
    if (w.shift_id && !p.shift_ids?.includes(w.shift_id)) (p.shift_ids ??= []).push(w.shift_id);
  }
  return [...groups.values()]
    .map(({ by: _by, ...g }) => {
      // The detail is only worth showing when it differs between people.
      // Several days with the same expiry/leave type: say it once ("Mon 19 Oct, Tue 20 Oct · expired 10 Oct 2026").
      for (const p of g.people) p.details = factor(p.details);
      const all = g.people.flatMap(p => p.details);
      if (g.people.length > 1 && all.length && all.every(x => x === all[0])) g.people.forEach(p => { p.details = []; });
      return g;
    })
    .sort((a, b) => RANK[a.severity] - RANK[b.severity] || b.people.length - a.people.length || a.title.localeCompare(b.title));
}

function factor(details: string[]): string[] {
  if (details.length < 2) return details;
  const parts = details.map(d => d.split(' · '));
  if (!parts.every(x => x.length === 2)) return details;
  if (parts.every(x => x[1] === parts[0][1])) return [`${parts.map(x => x[0]).join(', ')} · ${parts[0][1]}`];
  if (parts.every(x => x[0] === parts[0][0])) return [`${parts[0][0]} · ${parts.map(x => x[1]).join(', ')}`];
  return details;
}

/**
 * t242u: the Warnings bar's one-line summary, e.g. "3 clashes · 2 rest-time · 1 position not allowed".
 * Counts warnings (not people) per kind, worst severity first, then most first.
 */
export interface WarningCount { key: string; label: string; count: number; severity: WtrSeverity }
const KIND: Record<WtrCode, [key: string, one: string, many: string]> = {
  overlap: ['clash', 'clash', 'clashes'], duplicate: ['clash', 'clash', 'clashes'],
  rest_break: ['rest', 'rest-time', 'rest-time'], daily_rest: ['rest', 'rest-time', 'rest-time'], weekly_rest: ['rest', 'rest-time', 'rest-time'],
  weekly_hours: ['hours', 'over hours', 'over hours'], average_48h: ['hours', 'over hours', 'over hours'], max_hours_preference: ['hours', 'over hours', 'over hours'],
  young_daily_limit: ['young', 'young worker', 'young worker'], young_weekly_limit: ['young', 'young worker', 'young worker'], young_night_work: ['young', 'young worker', 'young worker'],
  unavailable: ['unavailable', 'unavailable', 'unavailable'], on_leave: ['leave', 'on leave', 'on leave'],
  missing_skill: ['skill', 'skill gap', 'skill gaps'], position_not_at_building: ['position', 'position not allowed', 'positions not allowed'],
  closure_day: ['closure', 'closure day', 'closure days'], dst_adjusted: ['dst', 'clock change', 'clock changes'],
  contract_hours: ['hours', 'over hours', 'over hours'], contract_days: ['days', 'over contract days', 'over contract days'],
};
export function warningSummary(warnings: WtrWarning[]): WarningCount[] {
  const by = new Map<string, WarningCount & { one: string; many: string }>();
  for (const w of warnings) {
    const [key, one, many] = KIND[w.code] ?? ['other', 'other', 'other'];
    const c = by.get(key) ?? { key, label: '', count: 0, severity: w.severity, one, many };
    c.count++;
    if (RANK[w.severity] < RANK[c.severity]) c.severity = w.severity;
    by.set(key, c);
  }
  return [...by.values()]
    .map(({ one, many, ...c }) => ({ ...c, label: `${c.count} ${c.count === 1 ? one : many}` }))
    .sort((a, b) => RANK[a.severity] - RANK[b.severity] || b.count - a.count || a.label.localeCompare(b.label));
}

/**
 * t249u: what's wrong with one shift, for the ⚠ popover (phone and desktop): one short line per kind of warning,
 * worst first, using the same titles as the Warnings bar. The detail drops the shift's own date (it's on the card).
 */
export interface ShiftWarningLine { key: string; severity: WtrSeverity; title: string; detail: string | null }
export function shiftWarningLines(warnings: readonly WtrWarning[]): ShiftWarningLine[] {
  const out = new Map<string, ShiftWarningLine>();
  for (const w of warnings) {
    if (w.severity === 'info') continue;
    const c = classify(w);
    const date = str(w.details?.date);
    const day = date ? shiftDay(date) : null;
    const detail = c.detail ? c.detail.split(' · ').filter(x => x !== day).join(' · ') || null : null;
    const key = `${c.key}|${detail ?? ''}`;
    const cur = out.get(key);
    if (!cur) out.set(key, { key, severity: w.severity, title: c.title, detail: detail === c.title ? null : detail });
    else if (RANK[w.severity] < RANK[cur.severity]) cur.severity = w.severity;
  }
  return [...out.values()].sort((a, b) => RANK[a.severity] - RANK[b.severity] || a.title.localeCompare(b.title));
}
